import { call, fork, put, select, take, takeEvery, throttle } from 'redux-saga/effects';
import differenceInMinutes from 'date-fns/differenceInMinutes';

import { interval } from 'helpers/saga';
import request from 'helpers/api';
import { decodeToken } from 'helpers/auth';
import { clearLocalStorage } from 'helpers/localstorage-sync';
import history from 'helpers/history';

import { cancelInitialize, initialize, setSidebar } from 'redux/reducers/app'
import {
  ADD_CHILD,
  clearAuthToken,
  clearProfile,
  failedLogin,
  failedPasswordReset,
  failedRegister,
  LOGIN,
  LOGOUT,
  logout,
  REGISTER,
  REQUEST_RESET_PASSWORD,
  RESET_PASSWORD,
  setAuthToken,
  trackEvent,
  UPDATE_PROFILE,
  updateChildren,
  updateProfile,
} from 'redux/reducers/user';
import { SET_OPTION, setOption } from '../reducers/orders';
import { fromJS, Map } from 'immutable';
import { getOrder } from '../selectors/orders';
import { getActiveOrderId } from '../selectors/app';

const RESET_OFFSET = 60; // reset token when it expires in an hour or less
const CHECK_INTERVAL = 10; // Seconds to check if the token is expired

export function* passwordReset({ user_id, token: resetToken, password }) {
  try {
    const { token, profile } = yield call(request, 'user/password-reset', {
      body: {
        user_id,
        token: resetToken,
        password,
      },
    });

    // Failed
    if (!token) {
      yield put(failedPasswordReset());
    }
    // Success
    else {
      yield put(setAuthToken(token));
      yield put(updateProfile(profile));
      yield put(setSidebar('myOrders'));

      history.replace('/');
    }
  } catch (e) {
    pe(e);
  }
}

export function* requestPasswordReset({ email }) {
  try {
    const req = yield call(request, 'user/password-request', {
      body: {
        email,
      },
    });
  } catch (e) {
    pe(e);
  }
}

export function* doRegister({ first_name, email, password, successScreen }) {
  try {
    const giftcard = yield select(state => state.getIn(['giftcards', 'code'], null));

    const { code, token, profile, orderId, bookId } = yield call(request, 'user/register', {
      body: {
        first_name,
        email,
        password,
        giftcard,
      },
    });

    // Failed
    if (code) {
      yield put(failedRegister(code));
    }
    // Success
    else {
      yield put(setAuthToken(token));
      yield put(updateProfile(profile));

      if (orderId) {
        yield put(cancelInitialize())
        yield put(initialize(bookId, orderId, false, 'saveSuccess'));
      }

      yield put(setSidebar(successScreen));
    }
  } catch (e) {
    pe(e);
  }
}

export function* doLogin({ email, password }) {
  try {
    const giftcard = yield select(state => state.getIn(['giftcards', 'code'], null));

    const { code, token, profile, bookId, orderId } = yield call(request, 'user/login', {
      body: {
        email,
        password,
        giftcard,
      },
    });

    // Failed
    if (code) {
      yield put(failedLogin(code));
    }
    // Success
    else {
      yield put(setAuthToken(token));
      yield put(updateProfile(profile));

      if (orderId) {
        yield put(cancelInitialize())
        yield put(initialize(bookId, orderId, false, 'saveSuccess'));
        yield put(setSidebar('photos'));
      } else {
        yield put(setSidebar('myOrders'));
      }
    }
  } catch (e) {
    pe(e);
  }
}

export function* doLogout() {
  try {
    const email = yield select(state => state.getIn(['user', 'email'], null));
    yield put(
      trackEvent('logged_out', {
        email,
      })
    );

    yield put(clearAuthToken());
    yield put(clearProfile());

    clearLocalStorage();
  } catch (e) {
    pe(e);
  } finally {
    if (DEPLOY_ENV !== 'development' && window && window.location) {
      window.location.replace('https://kroost.nl');
    } else {
      history.replace('/');
      window.onbeforeunload = null;
      window.location.reload();
    }
  }
}

export function* getProfile() {
  try {
    const token = yield select(state => state.getIn(['user', 'token'], null));
    if (token) {
      const profile = yield call(request, 'user/profile', {
        auth: true,
      });

      yield put(updateProfile(profile));
      // yield put(updateAnalyticsProfile())
    }
  } catch (e) {
    pe(e);

    // Force logout when we get 4xx error while saving
    if (e >= 400 && e < 500) {
      yield put(logout());
    }
  }
}

function* doGetChildren() {
  try {
    const token = yield select(state => state.getIn(['user', 'token'], null));
    if (token) {
      const children = yield call(request, 'user/children', {
        auth: true,
      });

      yield put(updateChildren(children));
    }
  } catch (e) {
    pe(e);
  }
}

function* addChild({ name, birthday }) {
  try {
    const token = yield select(state => state.getIn(['user', 'token'], null));
    if (token) {
      const newChild = yield call(request, 'user/child_add', {
        method: 'post',
        auth: true,
        body: { name, birthday },
      });

      const orderId = yield select(state => state.getIn(['app', 'activeOrderId']));
      const child = yield select(state => state.getIn(['orders', orderId, 'child']));

      yield put(
        setOption(orderId, 'child', fromJS(newChild), {
          previousId: child.get('id'),
          previousName: child.get('name'),
          previousBirthDay: child.get('birthday'),
        })
      );

      yield doGetChildren();
    }
  } catch (e) {
    pe(e);
  }
}

function* updateChild({ option, orderId, value, ...rest }) {
  if (option.length === 2 && option[0] === 'child' && (option[1] === 'name' || option[1] === 'birthday')) {
    const updateKey = option[1];

    const order = yield select(getOrder);
    const id = order.getIn(['child', 'id']);

    const children = yield select(state => state.getIn(['user', 'children']));
    const newChildren = children.toJS().map(child =>
      child.id === id
        ? {
            ...child,
            [updateKey]: value,
          }
        : child
    );

    yield put(updateChildren(newChildren));
  }
}

function* updateChildTrack({ option, orderId, value, ...rest }) {
  if (option.length === 2 && option[0] === 'child' && (option[1] === 'name' || option[1] === 'birthday')) {
    console.log('updateChildTrack');
    const updateKey = option[1];

    const order = yield select(state => state.getIn(['orders', orderId], Map()));
    const id = order.getIn(['child', 'id']);

    const children = yield select(state => state.getIn(['user', 'children']));
    const newChildren = children.toJS().map(child =>
      child.id === id
        ? {
            ...child,
            [updateKey]: value,
          }
        : child
    );

    const newChild = newChildren.find(child => child.id === id);

    yield put(
      trackEvent('child_edited', {
        child_id: newChild.id,
        child_name: newChild.name,
        child_dob: newChild.birthday,
      })
    );
  }
}

function* refreshToken({ decodedToken }) {
  try {
    const {
      data: { refresh_token, user_id },
    } = decodedToken;
    const { valid, token: newToken } = yield call(request, 'user/token/refresh', {
      auth: true,
      body: {
        onlyWhenExpired: false,
        refresh_token: refresh_token,
        user_id,
      },
    });

    // Update token and continue like normal
    if (newToken) {
      yield put(setAuthToken(newToken));
    }
    // If token isn't valid anymore, do a forces logout
    else if (!valid) {
      yield put(logout());
    }
  } catch (e) {
    pe(e);
  }
}

function* refreshTokenTimer() {
  const chan = yield call(interval, CHECK_INTERVAL);

  while (true) {
    yield take(chan);

    try {
      const token = yield select(state => state.getIn(['user', 'token'], null));

      if (token) {
        const decodedToken = decodeToken(token);
        const { exp } = decodedToken;

        const expiresInMinutes = differenceInMinutes(new Date(exp * 1000), new Date());

        if (expiresInMinutes < RESET_OFFSET) {
          yield call(refreshToken, {
            decodedToken,
            forceRefresh: true,
          });
        }
      }
    } catch (e) {
      pe(e);
    }
  }
}

export default function* saga() {
  yield takeEvery(REQUEST_RESET_PASSWORD, requestPasswordReset);
  yield takeEvery(RESET_PASSWORD, passwordReset);
  yield takeEvery(REGISTER, doRegister);
  yield takeEvery(LOGIN, doLogin);
  yield takeEvery(LOGOUT, doLogout);
  yield takeEvery(UPDATE_PROFILE, doGetChildren);
  yield takeEvery(ADD_CHILD, addChild);
  yield takeEvery(SET_OPTION, updateChild);
  yield throttle(10000, SET_OPTION, updateChildTrack);

  yield fork(getProfile);
  yield fork(refreshTokenTimer);
  // yield takeEvery(REGISTER, doRegister);
}



// WEBPACK FOOTER //
// ./app/redux/sagas/user.js